// RadioButtonGroup.tsx
interface RadioButtonGroupProps<T extends string | number | symbol> {
    options: T[];
    selectedValue: T | null;
    onChange: (value: T) => void;
    optionLabels?: { [key in T]?: string };
    disabled?: boolean;
    name: string;
    id?: string;
}

const RadioButtonGroup = <T extends string>({
    options,
    selectedValue,
    onChange,
    optionLabels,
    disabled,
    name,
    id,
}: RadioButtonGroupProps<T>) => {
    return (
        <div className="flex flex-row gap-4">
            {options.map((value) => {
                const isChecked = selectedValue === value;
                const isDisabled = disabled;
                const label = optionLabels?.[value] ?? value;
                const inputId = `${id ?? name}-${value}`;

                return (
                    <label
                        key={value}
                        htmlFor={inputId}
                        className={[
                            'flex items-center gap-2.5 w-fit cursor-pointer select-none',
                            isDisabled ? 'opacity-50 cursor-not-allowed' : 'group',
                        ].join(' ')}
                    >
                        <input
                            disabled={isDisabled}
                            type="radio"
                            name={name}
                            id={inputId}
                            value={value}
                            checked={isChecked}
                            onChange={() => onChange(value)}
                            className="absolute opacity-0 w-0 h-0 pointer-events-none"
                        />
                        <span
                            className={[
                                'flex-shrink-0 w-5 h-5 rounded-full border flex items-center justify-center transition-all duration-200',
                                isChecked
                                    ? 'border-brandPrimary-600 bg-white'
                                    : 'border-neutral-300 bg-white',
                                !isDisabled && !isChecked
                                    ? 'group-hover:border-brandPrimary-500 group-hover:shadow-[0_0_0_4px_rgba(77,121,127,0.08)]'
                                    : '',
                            ].join(' ')}
                        >
                            <span
                                className={[
                                    'w-2 h-2 rounded-full bg-brandPrimary-600 transition-all duration-200',
                                    isChecked ? 'scale-100 opacity-100' : 'scale-0 opacity-0',
                                ].join(' ')}
                            />
                        </span>
                        <span
                            style={{ fontFamily: 'Manrope' }}
                            className={[
                                'text-sm leading-5 transition-colors duration-200',
                                isChecked
                                    ? 'text-brandPrimary-800 font-semibold'
                                    : 'text-neutral-600 font-medium',
                            ].join(' ')}
                        >
                            {label}
                        </span>
                    </label>
                );
            })}
        </div>
    );
};

export default RadioButtonGroup;